const TARGET_STORAGE_KEY = "deepdd_api_targets";
const DEFAULT_TARGETS = {
  environment: "test",
  test_url: process.env.REACT_APP_DEEP_DD_TEST_API_BASE || "https://dev2.4pay.ca/lab/dociify3",
  production_url: process.env.REACT_APP_DEEP_DD_PRODUCTION_API_BASE || "https://dev2.4pay.ca/dociify3",
};

export const getDeepDDTargets = () => {
  try {
    const saved = JSON.parse(window.localStorage.getItem(TARGET_STORAGE_KEY) || "{}");
    return { ...DEFAULT_TARGETS, ...saved };
  } catch (error) {
    return DEFAULT_TARGETS;
  }
};

export const saveDeepDDTargets = (targets) => {
  const normalized = {
    environment: targets.environment === "production" ? "production" : "test",
    test_url: String(targets.test_url || DEFAULT_TARGETS.test_url).replace(/\/$/, ""),
    production_url: String(targets.production_url || DEFAULT_TARGETS.production_url).replace(/\/$/, ""),
  };
  window.localStorage.setItem(TARGET_STORAGE_KEY, JSON.stringify(normalized));
  window.dispatchEvent(new CustomEvent("deepdd-api-target-changed", { detail: normalized }));
  return normalized;
};

export const getDeepDDBase = () => {
  const targets = getDeepDDTargets();
  const base = targets.environment === "production"
    ? targets.production_url
    : targets.test_url;
  return String(base || DEFAULT_TARGETS.test_url).replace(/\/$/, "");
};

const parseResponse = async (response) => {
  const text = await response.text();
  let data;

  try {
    data = text ? JSON.parse(text) : {};
  } catch (error) {
    throw new Error(`DeepDD returned invalid JSON: ${text.slice(0, 180)}`);
  }

  if (!response.ok || data?.ok === false) {
    throw new Error(data?.error || `DeepDD request failed (${response.status})`);
  }

  return data;
};

export const deepDDFetch = async (path, options = {}) => {
  const response = await fetch(`${getDeepDDBase()}${path}`, {
    ...options,
    headers: {
      Accept: "application/json",
      // The PHP services read the raw request body, so text/plain keeps these
      // cross-origin JSON calls simple while preserving the same payload.
      ...(options.body ? { "Content-Type": "text/plain;charset=UTF-8" } : {}),
      ...(options.headers || {}),
    },
  });

  return parseResponse(response);
};

export const postDeepDD = (path, payload) =>
  deepDDFetch(path, {
    method: "POST",
    body: JSON.stringify(payload),
  });

export const getHistory = () => deepDDFetch("/ui/history_api.php");

export const initializeInvestigation = (subject) =>
  postDeepDD("/run/run_step1.php", subject);

export const runWorkflowStep = async (step, investigationId, subject) => {
  if (step.key === "initialize") {
    return initializeInvestigation(subject);
  }

  const query = `?investigation_id=${encodeURIComponent(investigationId)}`;
  const options = { method: step.method };

  if (step.method === "POST") {
    options.body = JSON.stringify({ investigation_id: investigationId });
  }

  return deepDDFetch(`${step.path}${query}`, options);
};

export const hubSpotRequest = (payload) =>
  postDeepDD("/ui/hubspot_api.php", payload);

export const WORKFLOW_STEPS = [
  { key: "initialize", number: "1", label: "Subject", method: "POST", path: "/run/run_step1.php" },
  { key: "discovery", number: "2", label: "Discovery", method: "POST", path: "/run/run_step2_discovery.php" },
  { key: "crawl", number: "3", label: "Crawl", method: "POST", path: "/run/run_step3_crawl.php" },
  { key: "extract", number: "4", label: "Extract", method: "POST", path: "/run/run_step4_extract.php" },
  { key: "leadership", number: "5", label: "Leadership", method: "GET", path: "/run/run_step5_leadership.php" },
  { key: "social", number: "6A", label: "Social", method: "GET", path: "/run/run_step6_social.php" },
  { key: "military", number: "6B", label: "Military", method: "POST", path: "/run/run_step6b_military.php" },
  { key: "darkweb", number: "6C", label: "Dark Web", method: "POST", path: "/run/run_step6c_darkweb.php" },
  { key: "domain", number: "7", label: "Domain", method: "GET", path: "/run/run_step7_domain.php" },
  { key: "ofac", number: "8", label: "OFAC", method: "GET", path: "/run/run_step8_ofac.php" },
  { key: "ai", number: "9", label: "AI Review", method: "GET", path: "/run/run_step9_ai.php" },
  { key: "risk", number: "10", label: "Risk", method: "GET", path: "/run/run_step10_risk.php" },
  { key: "report", number: "11", label: "Report", method: "GET", path: "/run/run_step11_report.php" },
];
